import { useCallback, type ReactNode } from "react";

import clsx from "clsx";

import { Tooltip } from "@components/tooltip-container";
import { Badge, TertiaryButton } from "@components/v2";
import { type CellContext } from "@tanstack/react-table";

interface TableCellProps<TData> {
  cell: CellContext<TData, unknown>;
  items?: string[];
}

const BadgeCellFormatter = <TData,>(props: TableCellProps<TData>) => {
  const isArray = props.items
    ? Array.isArray(props.items)
    : Array.isArray(props.cell.getValue());
  const value = props.items ? props.items : props.cell.getValue();

  return (
    <div
      className={clsx(
        "px-4 py-2.5 bg-inherit font-mono h-full",
        "text-xs text-v2-action font-medium tracking-[0.28px]",
        "flex justify-center items-center",
      )}
    >
      {value && (value?.length || 0) > 0 ? (
        <TagsTooltip value={value}>
          <Badge variant="information">
            {isArray ? (value as string[]).length : String(value)}
          </Badge>
        </TagsTooltip>
      ) : (
        "-"
      )}
    </div>
  );
};

const TagsTooltip = ({
  value,
  children,
}: {
  value: unknown;
  children: ReactNode;
}) => {
  const isArray = Array.isArray(value);

  const handleCopy = useCallback(() => {
    if (Array.isArray(value)) {
      const textToCopy = (value as string[]).join(", ");
      navigator.clipboard.writeText(textToCopy);
    }
  }, [value]);

  if (!isArray) {
    return children;
  }

  return (
    <Tooltip
      noInfoCircle
      overlayClassName="bg-v2-secondary p-2"
      content={
        <div className="">
          <div className="border border-v2-tertiary rounded-[4px] grid grid-cols-[30px_1fr] bg-v2-primary overflow-hidden">
            {(value as string[]).map((tag, index) => (
              <div key={`${tag}-${index}`} className="contents">
                <div
                  className={clsx(
                    "text-v2-action font-mono font-bold text-2xs border-r border-b border-v2-tertiary h-full w-full flex-center",
                    { "border-b-0": index === (value as string[]).length - 1 },
                  )}
                >
                  {index + 1}
                </div>
                <div
                  className={clsx(
                    "py-1 border-b border-v2-tertiary px-3 text-xs text-v2-primary flex items-center gap-2 max-w-[440px] break-all",
                    { "border-b-0": index === (value as string[]).length - 1 },
                  )}
                >
                  {tag}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-stretch justify-end">
            <TertiaryButton onClick={handleCopy}>
              <i className="fa-regular fa-copy"></i>
              Copy
            </TertiaryButton>
          </div>
        </div>
      }
    >
      {children}
    </Tooltip>
  );
};

export default BadgeCellFormatter;
